import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { getDetailedEntries } from "@/lib/detailedEntryLogger";
import { useQuery } from "@tanstack/react-query";
import { X, Pencil } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { supabase } from "@/integrations/supabase/client";
import { convertFromBase, getMetricBaseUnit } from "@/lib/unitConversions";
import type { UnitType } from "@/lib/unitConversions";

interface DailyEntriesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  currentUserId?: string; // ID of the viewer (to determine if viewing own data)
  entryDate: string; // ISO format YYYY-MM-DD
  metricKey: string;
  metricName: string;
  metricEmoji: string;
  metricUnit?: string;
  isBoolean?: boolean;
  isTutorialFake?: boolean;
}

export const DailyEntriesModal = ({

  open,
  onOpenChange,
  userId,
  currentUserId,
  entryDate,
  metricKey,
  metricName,
  metricEmoji,
  metricUnit = "",
  isBoolean = false,
  isTutorialFake = false,
}: DailyEntriesModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const isOwnData = currentUserId === userId;

  // Handle edit button click - navigate to track page with the date and metric
  const handleEditClick = () => {
    onOpenChange(false); // Close the modal
    // Navigate to track page with the date and metric key
    navigate("/track", {
      state: {
        selectedDate: entryDate,
        openMetric: metricKey,
      },
    });
  };

  // Fetch detailed entries for this date and metric
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["detailed-entries", userId, entryDate, metricKey, isTutorialFake],
    queryFn: () => {
      if (isTutorialFake) {
        // Return fake data for tutorial with translated entry texts
        return Promise.resolve([
          { id: 'fake-1', entry_time: new Date().toISOString(), entry_text: t('onboarding.integrated_tutorial.fake_entry_breakfast' as any) },
          { id: 'fake-2', entry_time: new Date().toISOString(), entry_text: t('onboarding.integrated_tutorial.fake_entry_snack' as any) },
          { id: 'fake-3', entry_time: new Date().toISOString(), entry_text: t('onboarding.integrated_tutorial.fake_entry_lunch' as any) },
        ]);
      }
      return getDetailedEntries({ userId, entryDate, metricKey });
    },
    enabled: open,
  });

  // Fetch daily entry total and goal (skip for boolean metrics or tutorial fake)
  const { data: dailyData } = useQuery({
    queryKey: ["daily-entry-total", userId, entryDate, metricKey, isTutorialFake],
    queryFn: async () => {
      if (isBoolean || isTutorialFake) return null;

      // Fetch the daily entry for this date
      const { data: dailyEntry } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", userId)
        .eq("entry_date", entryDate)
        .single();

      // Fetch the user's goal
      const { data: goalData } = await supabase
        .from("user_goals")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (!dailyEntry) return null;

      // Get the value for this metric
      let value: number | null = null;
      const goalKey = metricKey.replace("_value", "_goal");
      const goal = goalData?.[goalKey] ?? null;

      // Handle different metric key formats
      if (metricKey.startsWith("custom_") && metricKey.endsWith("_value")) {
        value = dailyEntry[metricKey];
      } else {
        value = dailyEntry[metricKey];
      }

      return { value, goal };
    },
    enabled: open && !isBoolean,
  });

  // Control visibility for slide-in animation
  useEffect(() => {
    if (open) {
      // Small delay to allow the dialog to mount
      const timer = setTimeout(() => setIsVisible(true), 50);
      
      // Scroll down slightly after panel opens to help with focus issues (tutorial only)
      let scrollTimer: NodeJS.Timeout | undefined;
      if (isTutorialFake) {
        scrollTimer = setTimeout(() => {
          window.scrollBy({ top: 1, behavior: 'smooth' });
          // Scroll back to maintain position
          setTimeout(() => {
            window.scrollBy({ top: -1, behavior: 'smooth' });
          }, 100);
        }, 500);
      }
      
      return () => {
        clearTimeout(timer);
        if (scrollTimer) clearTimeout(scrollTimer);
      };
    } else {
      setIsVisible(false);
    }
  }, [open, isTutorialFake]);

  const formatEntryTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Guard against invalid dates
  if (!open || !entryDate || !userId) return null;

  const formattedDate = format(new Date(entryDate + "T00:00:00"), "MMMM d, yyyy");

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={() => onOpenChange(false)}
          />

          {/* Bottom Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[101] bg-background rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col daily-entries-modal-content"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-2xl">{metricEmoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-semibold truncate">{metricName}</div>
                  <div className="text-sm text-muted-foreground">
                    {formattedDate}
                  </div>
                  {/* Show total if we have daily data */}
                  {!isBoolean && dailyData?.value !== null && dailyData?.value !== undefined && (() => {
                    // Convert values if user prefers a different unit than base
                    const baseUnit = getMetricBaseUnit(metricKey);
                    const needsConversion = metricUnit && metricUnit !== baseUnit;
                    const displayValue = needsConversion 
                      ? convertFromBase(dailyData.value, metricKey, metricUnit as UnitType)
                      : dailyData.value;
                    const displayGoal = needsConversion && dailyData.goal
                      ? convertFromBase(dailyData.goal, metricKey, metricUnit as UnitType)
                      : dailyData.goal;
                    
                    // Format numbers nicely (remove unnecessary decimals)
                    const formatNum = (n: number) => Number.isInteger(n) ? n : n.toFixed(1);
                    
                    return (
                      <div className="text-xs font-medium text-foreground mt-1">
                        Total: {formatNum(displayValue)}{metricUnit ? ` ${metricUnit}` : ""}
                        {displayGoal && ` / ${formatNum(displayGoal)}${metricUnit ? ` ${metricUnit}` : ""}`}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Edit button - only show for own data and not tutorial */}
                {isOwnData && !isTutorialFake && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleEditClick}
                    title={t('common.edit')}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Scrollable entries list */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isVisible ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-1 overflow-y-auto space-y-2 p-4"
            >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <MiniGridLoader className="w-6 h-6 text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            // For HealthKit metrics, show synced value even if no detailed entries exist
            metricKey.startsWith('healthkit_') && dailyData?.value != null ? (
              <div className="space-y-2">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-3 rounded-lg bg-muted/50 border border-border/50"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg mt-0.5">🍎</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground leading-relaxed">
                        Synced from Apple Health
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(() => {
                          const val = dailyData.value;
                          if (metricKey === 'healthkit_steps') return `${Number(val).toLocaleString()} steps`;
                          if (metricKey === 'healthkit_sleep_time') return `${Number(val).toFixed(1)} hrs sleep`;
                          if (metricKey === 'healthkit_exercise_time') return `${val} min exercise`;
                          if (metricKey === 'healthkit_stand_hours') return `${val} stand hours`;
                          if (metricKey === 'healthkit_active_energy') return `${val} kcal active energy`;
                          return `${val}${metricUnit ? ` ${metricUnit}` : ''}`;
                        })()}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            ) : !currentUserId ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <p className="text-sm text-muted-foreground text-center">
                  {t('ui.text.create_account_to_view_entries', 'To view detailed entries, create a free account')}
                </p>
                <Button
                  size="sm"
                  onClick={() => {
                    onOpenChange(false);
                    navigate('/onboarding');
                  }}
                >
                  {t('ui.buttons.create_free_account', 'Create Free Account')}
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-muted-foreground">{t('ui.text.no_detailed_entries_recorded_for_this_day')}</p>
              </div>
            )
          ) : (
            <AnimatePresence mode="sync">
              {entries.map((entry, index) => {
                // Extract value from entry text for progress calculation
                let entryValue = 0;
                let previousTotal = 0;
                let newTotal = 0;

                if (!isBoolean && dailyData?.value !== null && dailyData?.value !== undefined) {
                  // Parse the value from the entry text (e.g., "→ +12 g protein" or "Logged 20 g")
                  const valueMatch = entry.entry_text.match(/[+\-]?\s*(\d+(?:\.\d+)?)/);
                  if (valueMatch) {
                    entryValue = parseFloat(valueMatch[1]);
                    // If it has a + or → symbol, it's additive
                    if (entry.entry_text.includes('+') || entry.entry_text.includes('→')) {
                      // Calculate cumulative totals up to this entry
                      for (let i = 0; i <= index; i++) {
                        const match = entries[i].entry_text.match(/[+\-]?\s*(\d+(?:\.\d+)?)/);
                        if (match && (entries[i].entry_text.includes('+') || entries[i].entry_text.includes('→'))) {
                          if (i < index) previousTotal += parseFloat(match[1]);
                          if (i <= index) newTotal += parseFloat(match[1]);
                        }
                      }
                    } else if (entry.entry_text.toLowerCase().includes('logged')) {
                      // For "Logged X" format, treat as absolute value at that point
                      previousTotal = index > 0 ? newTotal : 0;
                      newTotal = entryValue;
                    }
                  }
                }

                const showProgress = !isBoolean && dailyData?.goal && entryValue > 0;
                const goal = dailyData?.goal || 1;
                const previousPercentage = Math.min((previousTotal / goal) * 100, 100);
                const newPercentage = Math.min((newTotal / goal) * 100, 100);

                return (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05, // Stagger animation
                      ease: "easeOut",
                    }}
                    className="group"
                  >
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      transition={{ duration: 0.2 }}
                      className="p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors border border-border/50"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg mt-0.5 flex-shrink-0">
                          {metricEmoji}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground leading-relaxed break-words">
                            {isOwnData ? entry.entry_text : entry.entry_text.replace(/^Logged /i, 'They logged ')}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatEntryTime(entry.entry_time)}
                          </p>
                          
                          {/* Progress bar showing before -> after */}
                          {showProgress && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                                <span>{previousTotal.toFixed(1)} → {newTotal.toFixed(1)} {metricUnit}</span>
                                <span>{newPercentage.toFixed(0)}%</span>
                              </div>
                              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-muted-foreground/30 to-primary relative">
                                  <motion.div
                                    initial={{ width: `${previousPercentage}%` }}
                                    animate={{ width: `${newPercentage}%` }}
                                    transition={{ duration: 0.5, delay: index * 0.05 + 0.2 }}
                                    className="h-full bg-primary"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </motion.div>

            {/* Entry count footer */}
            {!isLoading && entries.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: isVisible ? 1 : 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="pt-3 border-t border-border/50 text-center text-xs text-muted-foreground pb-4"
              >
                {entries.length} {entries.length === 1 ? "entry" : "entries"} recorded
              </motion.div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
